/**
 * Best-Execution Reporting view — phase 8 of broker integration.
 *
 * Renders the execution-quality report the back-end emits monthly to the
 * S3 Object-Lock audit bucket. In production the view hits
 *   GET /v1/reports/best-execution?period=2026-04
 * and the BAL lambda reads the JSONL from S3 + returns the computed
 * metrics. Here we compute from the local audit stream via seedDemoFills.
 *
 * Mirrors SEC Rule 606/607 and NGX X-Compliance reporting conventions.
 */
import { useMemo, useState } from 'react';
import { computeMetrics, summarize, seedDemoFills } from '../lib/best-execution';
import { BROKERS, brokerById, type BrokerId } from '../lib/brokers';
import { koboToNaira } from '../lib/format';

export function BestExecutionView() {
  const [period, setPeriod] = useState<string>(() => new Date().toISOString().slice(0, 7));
  const fills = useMemo(() => seedDemoFills(), []);
  const metrics = useMemo(() => computeMetrics(fills), [fills]);
  const summary = useMemo(() => summarize(fills), [fills]);

  return (
    <div className="be">
      <div className="be-head">
        <div>
          <h2>Best-Execution Report</h2>
          <div className="be-sub">
            SEC Rule 606/607 and NGX X-Compliance metrics across every linked
            broker. Monthly report emitted to audit bucket
            <code> s3://nigastock-audit/best-execution/.../{`{YYYY-MM}`}/report.jsonl</code>
            with 7-year retention.
          </div>
        </div>
        <div className="be-period">
          <label>Reporting period</label>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
        </div>
      </div>

      <div className="be-summary">
        <div className="be-card"><span className="k">Orders</span><strong>{summary.orderCount.toLocaleString('en-NG')}</strong></div>
        <div className="be-card"><span className="k">Fill rate</span><strong className={summary.fillRate >= 0.95 ? 'up' : summary.fillRate >= 0.85 ? '' : 'down'}>{(summary.fillRate * 100).toFixed(1)}%</strong></div>
        <div className="be-card"><span className="k">Avg slippage</span><strong className={summary.avgSlippageBps <= 5 ? 'up' : summary.avgSlippageBps <= 15 ? '' : 'down'}>{summary.avgSlippageBps >= 0 ? '+' : ''}{summary.avgSlippageBps.toFixed(1)}bps</strong></div>
        <div className="be-card"><span className="k">Price improvement</span><strong className="up">{(summary.priceImprovementPct * 100).toFixed(1)}%</strong></div>
        <div className="be-card"><span className="k">Notional</span><strong className="mono">{koboToNaira(summary.notionalKobo)}</strong></div>
        <div className="be-card"><span className="k">Total fees</span><strong className="mono down">{koboToNaira(summary.totalFeesKobo)}</strong></div>
      </div>

      <h3 className="be-h">Per-broker breakdown</h3>
      <div className="be-table">
        <div className="be-row be-row-head">
          <div>Broker</div><div>Orders</div><div>Filled</div><div>Fill rate</div>
          <div>Avg slippage</div><div>Price improvement</div><div>Avg latency</div>
          <div>Notional</div><div>Fees</div>
        </div>
        {metrics.map((m) => (
          <div key={m.brokerId} className="be-row">
            <div className="be-broker">{brokerById(m.brokerId as BrokerId)?.logoGlyph} {brokerById(m.brokerId as BrokerId)?.label}</div>
            <div className="mono">{m.orderCount}</div>
            <div className="mono">{m.filledCount}</div>
            <div className={'mono ' + (m.fillRate >= 0.95 ? 'up' : m.fillRate >= 0.85 ? '' : 'down')}>{(m.fillRate * 100).toFixed(1)}%</div>
            <div className={'mono ' + (m.avgSlippageBps <= 5 ? 'up' : m.avgSlippageBps <= 15 ? '' : 'down')}>{m.avgSlippageBps >= 0 ? '+' : ''}{m.avgSlippageBps.toFixed(1)}bps</div>
            <div className="mono up">{(m.priceImprovementPct * 100).toFixed(1)}%</div>
            <div className="mono">{m.avgLatencyMs}ms</div>
            <div className="mono">{koboToNaira(m.notionalKobo)}</div>
            <div className="mono down">{koboToNaira(m.totalFeesKobo)}</div>
          </div>
        ))}
        {metrics.length === 0 && <div className="bl-empty">No executions for this period.</div>}
      </div>

      <div className="be-legend">
        <strong>How to read this:</strong>
        <ul>
          <li><em>Slippage</em> is the signed difference between the fill price and the mark-at-decision. Positive values mean you paid up (BUY) or sold for less (SELL) than the price you saw when you clicked.</li>
          <li><em>Price improvement</em> is the fraction of limit orders that filled better than the limit. Higher is better.</li>
          <li><em>Avg latency</em> is the wall-clock time between order placement and first fill acknowledgement.</li>
          <li>Metrics over 15bps of slippage or below 85% fill rate de-rank that broker in the Smart Order Router's composite score for the next reporting cycle.</li>
        </ul>
      </div>
    </div>
  );
}
